import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Box, useTheme, Stack, Button, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { patch } from '@truefit/http-utils';
import { BackButton } from './BackButton';
import { SportEvent } from '../types/SportEventTypes';
import { EventInClipCreation } from './EventInClipCreation';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { useMediaContext } from '../context/MediaContext';
import { useFOVOverlay } from '../context/FOVOverlayContext';
import { ClipCreationForm } from './ClipCreationForm';
import useCreateCustomEventMutation from '../hooks/useCreateCustomEventMutation';
import useUpdateCustomEventMutation from '../hooks/useUpdateCustomEventMutation';
import useDeleteCustomEventMutation from '../hooks/useDeleteCustomEventMutation';
import { parsePlayByPlayEvent } from '../utils/sportEventUtils';
import { ConfirmationDialogProvider } from '@/shared/components';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import Tracking from '@/shared/services/tracking';
import type { ClipCameraSelection } from '@/features/clips/types';
import { getQueryKey as getClipGroupsQueryKey } from '@/features/clipGroups/hooks/usePlayByPlayClipGroupsQuery';

export interface ClipEventMetadata {
  teamLogoPath: string;
  gameTime: string;
  description: string;
}

interface ClipCreationContentProps {
  event: SportEvent | null;
  gameId: string;
  metadata: ClipEventMetadata | null;
  onClose: () => void;
  onEventUpdated?: () => void;
  onEventCreated?: (persistedEvent: SportEvent) => void;
  autoOpenFirstClip?: boolean;
}

export const ClipCreationContent: React.FC<ClipCreationContentProps> = ({
  event,
  gameId,
  metadata,
  onClose,
  onEventUpdated,
  onEventCreated,
  autoOpenFirstClip,
}) => {
  const theme = useTheme();
  const { availableCameras } = useServerStateContext();
  const currentGameId = useCurrentGameId();
  const queryClient = useQueryClient();

  const teamLogoPath = metadata?.teamLogoPath ?? '';
  const gameTime = metadata?.gameTime ?? '';
  const eventDescription = metadata?.description ?? '';

  // Drafts are local-only events that haven't been persisted yet. They have
  // no customEventId until the user commits a name via Enter / blur.
  const isDraftEvent = !!event && !(event as any).customEventId;

  // Mutations
  const createCustomEventMutation = useCreateCustomEventMutation();
  const updateCustomEventMutation = useUpdateCustomEventMutation();
  const deleteCustomEventMutation = useDeleteCustomEventMutation();

  // Treat empty string and the legacy 'New Custom Event' placeholder as
  // "not yet named". Anything else means the user has committed a name.
  const isDescriptionCommitted = (value: string) =>
    value.trim() !== '' && value.trim() !== 'New Custom Event';

  // State for tracking editing status
  const [hasBeenEdited, setHasBeenEdited] = useState(isDescriptionCommitted(eventDescription));
  const [currentDescription, setCurrentDescription] = useState(eventDescription);

  // Update currentDescription when eventDescription prop changes
  useEffect(() => {
    setCurrentDescription(eventDescription);
    setHasBeenEdited(isDescriptionCommitted(eventDescription));
  }, [eventDescription]);

  // Use MediaContext for state management
  const {
    clipGroups: allClipGroups,
    setSelectedEvent,
    updateClipGroupTime,
    addDraftClipGroup,
    updateTrackedObject,
    updateDraftOutputMode,
    createClipInGroup,
    deleteClipGroup: deleteClipGroupFromContext,
    refetchClipGroups,
    reset,
  } = useMediaContext();

  // Reset FOV overlay when clip creation panel closes
  const { setActivePortraitGroupId } = useFOVOverlay();
  useEffect(() => {
    return () => {
      setActivePortraitGroupId(null);
    };
  }, [setActivePortraitGroupId]);

  // Initialize context when content mounts with a persisted event. Drafts
  // (id == null) are intentionally skipped: MediaContext queries are gated on
  // selectedEventId, and there is nothing on the backend to query for yet.
  useEffect(() => {
    if (event?.id && event.timestamp) {
      setSelectedEvent(event);
    }
  }, [event, setSelectedEvent]);

  // After the parent swaps a draft event for a persisted one, MediaProvider
  // remounts (its key includes event.id), so this effect lands fresh on the
  // new mount. autoOpenFirstClip means: pivot the user into clip creation
  // straight away, without making them hunt for the "+ New Clip" button.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (
      autoOpenFirstClip &&
      !autoOpenedRef.current &&
      event?.timestamp &&
      allClipGroups.length === 0
    ) {
      const eventTime = new Date(event.timestamp);
      addDraftClipGroup({
        inTime: new Date(eventTime.getTime() - 5000),
        outTime: new Date(eventTime.getTime() + 5000),
        name: event.description?.trim() || undefined,
      });
      autoOpenedRef.current = true;
    }
  }, [autoOpenFirstClip, event, allClipGroups.length, addDraftClipGroup]);

  // Reset context when content unmounts
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Handlers now simply delegate to context methods
  const handleCameraClick = useCallback(
    (groupId: string | number, camera: ClipCameraSelection) => {
      createClipInGroup(groupId, camera);
    },
    [createClipInGroup],
  );

  const handleDeleteClipGroup = useCallback(
    (groupId: string | number) => {
      Tracking.getInstance().track('Delete Clip Group', {
        category: 'Clips',
      });
      deleteClipGroupFromContext(groupId);
    },
    [deleteClipGroupFromContext],
  );

  // Handle output mode (portrait/landscape) changes
  const handleOutputModeChange = useCallback(
    async (groupId: string | number, outputMode: 'portrait' | 'landscape') => {
      if (!event) {
        return;
      }

      // Handle draft clipGroups (local-only state)
      if (typeof groupId === 'string' && groupId.startsWith('draft-')) {
        await updateDraftOutputMode(groupId, outputMode);
        Tracking.getInstance().track('Change Output Mode', {
          category: 'Clips',
          mode: outputMode,
          isDraft: true,
        });
        return;
      }

      // A just-persisted draft is still the mounted item until the clip-group
      // refetch replaces it, and it renders from its LOCAL outputMode — so a
      // server-only PATCH here would leave the toggle visually frozen. Route
      // through the draft updater, which syncs local state and PATCHes the
      // persisted row itself.
      const transitionalDraft = allClipGroups.find(
        (g: any) =>
          typeof g.id === 'string' &&
          g.persistedId != null &&
          String(g.persistedId) === String(groupId),
      );
      if (transitionalDraft) {
        await updateDraftOutputMode(transitionalDraft.id as string, outputMode);
        Tracking.getInstance().track('Change Output Mode', {
          category: 'Clips',
          mode: outputMode,
          isDraft: true,
        });
        await refetchClipGroups();
        return;
      }

      const playbyplayId = (event as any).customEventId || (event as any).id;

      try {
        const result = await patch<{ success: boolean; clipGroup: any }>(
          `/v1.0/games/${currentGameId}/play-by-play/${playbyplayId}/clip-groups/${groupId}`,
          { outputMode }
        );
        Tracking.getInstance().track('Change Output Mode', {
          category: 'Clips',
          mode: outputMode,
        });

        // Invalidate the play-by-play clip groups query with the correct query key to force refetch
        const queryKey = getClipGroupsQueryKey(currentGameId, playbyplayId);
        queryClient.invalidateQueries({ queryKey });

        // Refetch clip groups to ensure UI is in sync with backend
        // We have the updated data in result.data.clipGroup, but refetch ensures consistency
        await refetchClipGroups();
      } catch (error) {
        console.error('Error changing output mode:', error);
        throw error;
      }
    },
    [
      event,
      currentGameId,
      setSelectedEvent,
      updateDraftOutputMode,
      refetchClipGroups,
      queryClient,
      allClipGroups,
    ],
  );

  // Handle inline description edit blur event.
  //
  // Drafts: the event has not been persisted yet. A real, committed name is
  // the trigger that creates the event on the backend. Empty/default values
  // are ignored — EventInClipCreation keeps the user in edit mode in that
  // case, so abandoning the drawer leaves no event behind.
  //
  // Persisted: existing update path.
  const handleDescriptionBlur = useCallback(
    async (newDescription: string) => {
      if (!event) return;

      const trimmedDescription = newDescription.trim();
      if (trimmedDescription === '' || trimmedDescription === 'New Custom Event') {
        return;
      }

      // Optimistic UI: surface the typed name immediately so the field stops
      // showing the placeholder while the create/update mutation is in flight.
      setCurrentDescription(trimmedDescription);
      setHasBeenEdited(true);

      if (isDraftEvent) {
        try {
          const created = await createCustomEventMutation.mutateAsync({
            timestamp: event.timestamp as any,
            eventType: 'custom',
            description: trimmedDescription,
          });
          const persistedEvent = parsePlayByPlayEvent(created);
          Tracking.getInstance().track('New Custom Event', { category: 'Clips' });
          onEventUpdated?.();
          onEventCreated?.(persistedEvent);
        } catch (error) {
          console.error('Error creating custom event:', error);
          setCurrentDescription('');
          setHasBeenEdited(false);
        }
        return;
      }

      const customEventId = (event as any).customEventId;
      if (!customEventId) return;

      if (trimmedDescription !== currentDescription) {
        try {
          await updateCustomEventMutation.mutateAsync({
            eventId: customEventId,
            data: { description: trimmedDescription },
          });
          Tracking.getInstance().track('New Custom Event', { category: 'Clips' });
          onEventUpdated?.();
        } catch (error) {
          console.error('Error updating custom event:', error);
        }
      }
    },
    [
      event,
      isDraftEvent,
      currentDescription,
      createCustomEventMutation,
      updateCustomEventMutation,
      onEventUpdated,
      onEventCreated,
    ],
  );

  // Close the drawer. Drafts are local-only, so there is nothing to clean up
  // on the backend. Persisted events always carry a real, user-committed
  // name by the time we get here, so we never auto-delete on close anymore.
  const handleClose = useCallback(() => {
    onEventUpdated?.();
    onClose();
  }, [onClose, onEventUpdated]);

  // Escape closes the drawer from anywhere on the page while it's open.
  // The listener is auto-scoped: it's only attached while this component is
  // mounted, which only happens when view.type === 'clip-creation'.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  const handleConfirmDelete = useCallback(async () => {
    if (!event || !(event as any).customEventId) return;

    try {
      await deleteCustomEventMutation.mutateAsync({
        eventId: (event as any).customEventId,
      });

      onClose();
      onEventUpdated?.();
    } catch (error) {
      console.error('Error deleting custom event:', error);
    }
  }, [event, deleteCustomEventMutation, onClose, onEventUpdated]);

  if (!event) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        backgroundColor: theme.palette.cx.bg.surface,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <ConfirmationDialogProvider
        dialogProps={{
          disablePortal: true,
          sx: { position: 'absolute', inset: 0 },
          slotProps: {
            backdrop: {
              sx: { position: 'absolute' },
            },
          },
        }}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
            }}
          >
            <BackButton onClick={handleClose} />
          </Box>

          {/* Event Info */}
          <EventInClipCreation
            event={event}
            teamLogoPath={teamLogoPath}
            gameTime={gameTime}
            description={currentDescription}
            onEdit={handleDescriptionBlur}
            onConfirmDelete={handleConfirmDelete}
          />
  
          {/* Clip Creation Form */}
          <SimpleBar style={{ flex: 1, minHeight: 0, overscrollBehavior: 'contain' }}>
            <ClipCreationForm
              clipGroups={allClipGroups}
              event={event}
              availableCameras={availableCameras}
              onTimeChange={updateClipGroupTime}
              onCameraClick={handleCameraClick}
              onDeleteClipGroup={handleDeleteClipGroup}
              onTrackedObjectChange={updateTrackedObject}
              onOutputModeChange={handleOutputModeChange}
            />

            {/* + Create Clip button - shown inside scroll area when no clips */}
            {allClipGroups.length === 0 &&  (
              <> 
                
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 0.5,
                  px: 1.5,
                }}
              >
                <Tooltip
                  title={
                    !hasBeenEdited ||
                    currentDescription === 'New Custom Event' ||
                    !currentDescription.trim()
                      ? 'Edit event name first'
                      : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      onClick={() => addDraftClipGroup()}
                      disabled={
                        !hasBeenEdited ||
                        currentDescription === 'New Custom Event' ||
                        !currentDescription.trim()
                      }
                      sx={{
                        py: 1.5,
                        px: 8,
                        textTransform: 'none',
                        borderRadius: '8px',
                        border: `1px dashed ${theme.palette.cx.border.default}`,
                        backgroundColor: '#17171C',
                        '&.Mui-disabled': {
                          opacity: 0.5,
                          color: 'rgba(255, 255, 255, 0.3)',
                          border: `1px dashed ${theme.palette.cx.border.subtle}`,
                        },
                      }}
                      variant="outlined"
                      color="inherit"
                    >
                      + New Clip
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              </>
           
            )}
          </SimpleBar>

          {/* + Create Clip button - shown sticky at bottom when there are clips */}
          {allClipGroups.length > 0 && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                py: 2,
                backgroundColor: theme.palette.cx.bg.surface,
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Tooltip
                title={
                  !hasBeenEdited ||
                  currentDescription === 'New Custom Event' ||
                  !currentDescription.trim()
                    ? 'Edit event name first'
                    : ''
                }
                arrow
              >
                <span>
                  <Button
                    onClick={() => addDraftClipGroup()}
                    disabled={
                      !hasBeenEdited ||
                      currentDescription === 'New Custom Event' ||
                      !currentDescription.trim()
                    }
                    sx={{
                      py: 1.5,
                      px: 8,
                      textTransform: 'none',
                      borderRadius: '8px',
                      border: `1px dashed ${theme.palette.cx.border.default}`,
                      backgroundColor: '#17171C',
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        color: 'rgba(255, 255, 255, 0.3)',
                        border: `1px dashed ${theme.palette.cx.border.subtle}`,
                      },
                    }}
                    variant="outlined"
                    color="inherit"
                  >
                    + New Clip
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Box>
      </ConfirmationDialogProvider>
    </Box>
  );
};

export default ClipCreationContent;
